const CACHE_KEY = 'remoteConfig';
const DEFAULT_CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes fallback
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class RemoteConfigManager {
    constructor() {
        this.config = null;
        this.loadPromise = null;
        this.cacheDurationMs = DEFAULT_CACHE_DURATION_MS;
    }

    getCachedConfig() {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (!cached) return null;

            const { data, timestamp, ttl } = JSON.parse(cached);
            const now = Date.now();
            const cacheDuration = ttl || DEFAULT_CACHE_DURATION_MS;

            // Check if cache is still valid
            if (now - timestamp < cacheDuration) {
                this.cacheDurationMs = cacheDuration;
                return data;
            }

            // Cache expired, remove it
            localStorage.removeItem(CACHE_KEY);
            return null;
        } catch (error) {
            console.error('Error reading cached config:', error);
            return null;
        }
    }

    setCachedConfig(data) {
        try {
            const cacheData = {
                data,
                timestamp: Date.now(),
                ttl: this.cacheDurationMs
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            console.error('Error caching config:', error);
        }
    }

    async fetchFromAPI() {
        try {
            const response = await fetch(`${API_BASE_URL}api/globalsettings/remote-config`);
            if (!response.ok) {
                throw new Error('Failed to fetch remote config');
            }
            const data = await response.json();

            // Update cache duration from API response
            if (data.cacheTTLMinutes) {
                this.cacheDurationMs = data.cacheTTLMinutes * 60 * 1000;
            }

            return data;
        } catch (error) {
            console.error('Error fetching remote config:', error);
            throw error;
        }
    }

    getDefaultConfig() {
        return {
            bannerMessage: '',
            maintenanceMode: 'false',
            tvAppPromptConfig: null,
            cmsWebpEnabled: null,
            cacheTTLMinutes: DEFAULT_CACHE_DURATION_MS / 60000
        };
    }

    async load() {
        // If already loading, return the existing promise
        if (this.loadPromise) {
            return this.loadPromise;
        }

        // Try to get from cache first
        const cached = this.getCachedConfig();
        if (cached) {
            this.config = cached;
            return cached;
        }

        // Fetch from API if not cached
        this.loadPromise = this.fetchFromAPI()
            .then(data => {
                this.config = data;
                this.setCachedConfig(data);
                this.loadPromise = null;
                return data;
            })
            .catch(error => {
                this.loadPromise = null;
                console.error('Failed to load remote config, using defaults');
                // If fetch fails, use default config
                this.config = this.getDefaultConfig();
                return this.config;
            });

        return this.loadPromise;
    }

    getConfig() {
        return this.config || this.getDefaultConfig();
    }

    isMaintenanceMode() {
        const config = this.getConfig();
        return config?.maintenanceMode === 'true' || config?.maintenanceMode === true;
    }

    getBannerMessage() {
        const config = this.getConfig();
        return config?.bannerMessage || '';
    }

    // Raw JSON string for the TV "Download our app" prompt (or null).
    getTvAppPrompt() {
        const config = this.getConfig();
        return config?.tvAppPromptConfig || null;
    }

    // Whether CMS images should be served as .webp siblings (see
    // helpers/cmsWebp.js). Off unless the CmsWebpEnabled global setting is
    // explicitly "true" — kept off until the blob backfill has run.
    isCmsWebpEnabled() {
        const config = this.getConfig();
        return config?.cmsWebpEnabled === 'true' || config?.cmsWebpEnabled === true;
    }

    getCacheTTLMinutes() {
        const config = this.getConfig();
        return config?.cacheTTLMinutes || (DEFAULT_CACHE_DURATION_MS / 60000);
    }

    async refetch() {
        this.loadPromise = null;
        try {
            const data = await this.fetchFromAPI();
            this.config = data;
            this.setCachedConfig(data);
            return data;
        } catch (error) {
            console.error('Failed to refetch remote config, using defaults');
            this.config = this.getDefaultConfig();
            return this.config;
        }
    }
}

// Export a singleton instance
export const remoteConfig = new RemoteConfigManager();